import { useEffect, useState } from 'react';
import { api, type UserRow } from '../../lib/api';
import { useUi, type NotificationLevel } from '@allrouted/ui/Toaster';
import { useAuth } from '../AuthGate';
import { ROLES } from './PeoplePage';
import { ago, errText, fullTime } from './AccountView';

/**
 * You: your name, your role, and your password.
 *
 * Changing your own password asks for the current one -- a session left open
 * on an unattended machine should not be enough to take the account -- and
 * signs out every other session you have.
 */
export function ProfilePage() {
  const ui = useUi();
  const { me } = useAuth();
  const [self, setSelf] = useState<UserRow | null>(null);
  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    void api
      .users()
      .then(({ users }) => {
        const mine = users.find((u) => u.id === me.user?.id) ?? null;
        setSelf(mine);
        setName(mine?.name ?? '');
      })
      .catch(() => {});
  }, [me.user?.id]);

  const role = ROLES.find((r) => r.value === me.user?.role);

  const saveName = async () => {
    if (!me.user) return;
    setSavingName(true);
    try {
      await api.updateUser(me.user.id, { name: name.trim() });
      setSelf((s) => (s ? { ...s, name: name.trim() || null } : s));
      ui.notify('ok', 'Name saved');
    } catch (err) {
      ui.notify('error', 'Not saved', errText(err));
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async () => {
    if (pw.next !== pw.confirm) {
      ui.notify('error', 'They do not match', 'The new password and its confirmation differ.');
      return;
    }
    if (pw.next.length < 10) {
      ui.notify('error', 'Too short', 'Ten characters is the minimum.');
      return;
    }
    setPwBusy(true);
    try {
      await api.changePassword(pw.current, pw.next);
      setPw({ current: '', next: '', confirm: '' });
      ui.notify('ok', 'Password changed', 'Every other session you had is signed out. This one stays.');
    } catch (err) {
      ui.notify('error', 'Could not change it', errText(err));
    } finally {
      setPwBusy(false);
    }
  };

  const nameDirty = name.trim() !== (self?.name ?? '');
  const canRename = me.user?.role !== 'viewer';

  return (
    <>
      <section className="acct-card">
        <h2 className="acct-card-title">Your details</h2>
        <div className="person-facts">
          <div className="fact">
            <span className="label">Email</span>
            <span>{me.user?.email}</span>
          </div>
          <div className="fact">
            <span className="label">Role</span>
            <span>
              {role?.label ?? me.user?.role}
              {role ? <span className="hint"> · {role.hint}</span> : null}
            </span>
          </div>
          <div className="fact">
            <span className="label">Signs in with</span>
            <span>{self ? [self.has_password ? 'Password' : null, self.has_google ? 'Google' : null].filter(Boolean).join(' · ') || '—' : '…'}</span>
          </div>
          <div className="fact">
            <span className="label">Member since</span>
            <span title={fullTime(self?.created_at ?? null)}>{self ? ago(self.created_at) : '…'}</span>
          </div>
        </div>
        {canRename ? (
          <div className="acct-inline-form">
            <label className="stack">
              <span className="label">Your name</span>
              <input className="field" value={name} placeholder="How your team sees you" onChange={(e) => setName(e.target.value)} />
            </label>
            <button type="button" className="btn btn-primary" disabled={!nameDirty || savingName} onClick={() => void saveName()}>
              {savingName ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : null}
      </section>

      <section className="acct-card">
        <h2 className="acct-card-title">Password</h2>
        <p className="hint">Changing it signs out every other session you have, everywhere.</p>
        <form
          className="acct-password"
          onSubmit={(e) => {
            e.preventDefault();
            void changePassword();
          }}
        >
          <input type="text" autoComplete="username" value={me.user?.email ?? ''} readOnly hidden />
          <label className="stack">
            <span className="label">Current password</span>
            <input
              className="field"
              type="password"
              autoComplete="current-password"
              value={pw.current}
              onChange={(e) => setPw((v) => ({ ...v, current: e.target.value }))}
            />
          </label>
          <label className="stack">
            <span className="label">New password</span>
            <input
              className="field"
              type="password"
              autoComplete="new-password"
              value={pw.next}
              onChange={(e) => setPw((v) => ({ ...v, next: e.target.value }))}
            />
          </label>
          <label className="stack">
            <span className="label">Repeat it</span>
            <input
              className="field"
              type="password"
              autoComplete="new-password"
              value={pw.confirm}
              onChange={(e) => setPw((v) => ({ ...v, confirm: e.target.value }))}
            />
          </label>
          <button type="submit" className="btn btn-primary" disabled={pwBusy || !pw.current || !pw.next}>
            {pwBusy ? 'Changing…' : 'Change password'}
          </button>
        </form>
        <p className="hint">
          Ten characters at least. Forgotten it? Sign out and use “Forgot password?” on the sign-in page.
        </p>
      </section>

      <NotificationsCard />
    </>
  );
}

const LEVELS: { value: NotificationLevel; label: string; hint: string }[] = [
  { value: 'all', label: 'All notifications', hint: 'A message for every action: “Stop moved”, “Order saved”, and errors.' },
  {
    value: 'important',
    label: 'Important only',
    hint: 'Errors, and messages that offer a next step, such as “Rebuild routes”. Routine confirmations stay quiet.',
  },
  { value: 'errors', label: 'Errors only', hint: 'Only when something goes wrong.' },
];

/**
 * Which notifications this person sees, saved with them so it follows them
 * to every browser. Errors always show, whatever is chosen here.
 */
function NotificationsCard() {
  const ui = useUi();
  const prefs = ui.notificationPrefs;

  return (
    <section className="acct-card">
      <h2 className="acct-card-title">Notifications</h2>
      <p className="hint">
        The messages in the corner after you do something. Errors always show. Saved to your user, so it applies on every
        computer you sign in from.
      </p>
      <div className="role-picker" role="radiogroup" aria-label="Which notifications to show">
        {LEVELS.map((l) => (
          <button
            key={l.value}
            type="button"
            role="radio"
            aria-checked={prefs.level === l.value}
            className="role-option"
            onClick={() => void ui.setNotificationPrefs({ ...prefs, level: l.value })}
          >
            <span className="role-radio" aria-hidden="true" />
            <span>
              <strong>{l.label}</strong>
              <small>{l.hint}</small>
            </span>
          </button>
        ))}
      </div>

      <div className="notif-muted">
        <span className="label">Muted messages</span>
        {prefs.muted.length === 0 ? (
          <p className="hint">None. Use “Mute these” on a notification to silence that kind of message.</p>
        ) : (
          <ul>
            {prefs.muted.map((kind) => (
              <li key={kind}>
                <span>{kind}</span>
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() => void ui.setNotificationPrefs({ ...prefs, muted: prefs.muted.filter((k) => k !== kind) })}
                >
                  Unmute
                </button>
              </li>
            ))}
          </ul>
        )}
        {prefs.muted.length > 1 ? (
          <div>
            <button type="button" className="btn btn-small" onClick={() => void ui.setNotificationPrefs({ ...prefs, muted: [] })}>
              Unmute all
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
